import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import LineItemsEditor from "@/components/LineItemsEditor";

const SOURCES = ["Direct", "Email", "Trade Fair", "Reference", "Website", "Cold Call", "Other"];

export default function InquiryForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [form, setForm] = useState({
    inquiry_date: new Date().toISOString().slice(0, 10),
    customer_id: "",
    plant_id: "",
    order_type: "New Manufacturing",
    customer_pr_number: "",
    dc_number: "",
    contact_person: { name: "", phone: "", email: "", designation: "" },
    sales_engineer_id: "",
    source: "Direct",
    expected_delivery: "",
    remarks: "",
    line_items: [{
      description: "", cylinder_type: "Hydraulic", manufacturing_type: "New", application: "",
      bore_mm: 100, rod_diameter_mm: 56, stroke_mm: 500, quantity: 1,
      operating_pressure_bar: 210, remarks: "",
    }],
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/auth/users").then((r) => setUsers(r.data.filter((u) => ["sales", "plant_head", "admin"].includes(u.role))));
    if (isEdit) {
      api.get(`/inquiries/${id}`).then((r) => {
        const d = r.data;
        setForm({
          inquiry_date: d.inquiry_date,
          customer_id: d.customer_id,
          plant_id: d.plant_id,
          order_type: d.order_type || "New Manufacturing",
          customer_pr_number: d.customer_pr_number || "",
          contact_person: d.contact_person || { name: "", phone: "", email: "", designation: "" },
          sales_engineer_id: d.sales_engineer_id || "",
          source: d.source || "Direct",
          expected_delivery: d.expected_delivery || "",
          remarks: d.remarks || "",
          line_items: d.line_items || [],
        });
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (form.customer_id) {
      api.get(`/plants?customer_id=${form.customer_id}`).then((r) => setPlants(r.data));
    } else {
      setPlants([]);
    }
  }, [form.customer_id]);

  async function submit(e) {
    e.preventDefault();
    if (!form.customer_id || !form.plant_id) {
      toast.error("Customer and Plant are required"); return;
    }
    if (!form.contact_person.name) {
      toast.error("Contact person name is required"); return;
    }
    if (form.line_items.length === 0) {
      toast.error("At least one line item is required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        expected_delivery: form.expected_delivery || null,
        sales_engineer_id: form.sales_engineer_id || null,
      };
      let res;
      if (isEdit) res = await api.patch(`/inquiries/${id}`, payload);
      else res = await api.post("/inquiries", payload);
      toast.success(`Inquiry ${res.data.inquiry_number} ${isEdit ? "updated" : "created"}`);
      nav(`/inquiries/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  return (
    <div data-testid="inquiry-form-page" className="max-w-5xl space-y-5">
      <div>
        <button onClick={() => nav("/inquiries")} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back to Inquiries
        </button>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
          {isEdit ? "Edit Inquiry" : "New Inquiry"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">Capture customer inquiry with all cylinder line items.</p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Basic */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Inquiry Details</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Inquiry Date *</Label>
              <Input type="date" data-testid="inq-date" value={form.inquiry_date}
                     onChange={(e) => setForm({ ...form, inquiry_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Customer PR/RFQ Number</Label>
              <Input data-testid="inq-pr-number" value={form.customer_pr_number}
                     onChange={(e) => setForm({ ...form, customer_pr_number: e.target.value })}
                     className="mt-1 font-mono" placeholder="e.g. PR-4589 / RFQ-00231 / ENQ/JSW/1456" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, plant_id: "" })}>
                <SelectTrigger data-testid="inq-customer" className="mt-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Plant *</Label>
              <Select value={form.plant_id} onValueChange={(v) => setForm({ ...form, plant_id: v })} disabled={!form.customer_id}>
                <SelectTrigger data-testid="inq-plant" className="mt-1"><SelectValue placeholder="Select plant" /></SelectTrigger>
                <SelectContent>
                  {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Sales Engineer</Label>
              <Select value={form.sales_engineer_id || "none"} onValueChange={(v) => setForm({ ...form, sales_engineer_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(Current user)</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Source *</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="inq-source" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Expected Delivery</Label>
              <Input type="date" value={form.expected_delivery}
                     onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Order Type *</Label>
              <Select value={form.order_type} onValueChange={(v) => setForm({ ...form, order_type: v })}>
                <SelectTrigger data-testid="inq-order-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="New Manufacturing">New Manufacturing</SelectItem>
                  <SelectItem value="Reconditioning - PO First">Reconditioning · PO First</SelectItem>
                  <SelectItem value="Reconditioning - Cylinder First">Reconditioning · Cylinder Received First</SelectItem>
                </SelectContent>
              </Select>
              {form.order_type === "Reconditioning - Cylinder First" && (
                <p className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Reconditioning against a Delivery Challan · you can capture the DC Number below and/or start from the <a href="/receiving" className="underline font-semibold">Receiving screen</a>. The inquiry stage itself is optional.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">DC Number <span className="text-slate-400 font-normal normal-case">(optional · for reconditioning)</span></Label>
              <Input
                data-testid="inq-dc-number"
                value={form.dc_number || ""}
                onChange={(e) => setForm({ ...form, dc_number: e.target.value })}
                placeholder="e.g. DC-1234 (leave blank if not applicable)"
                className="mt-1 font-mono"
              />
            </div>
          </div>
        </Card>

        {/* Contact */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Contact Person</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Name *</Label>
              <Input data-testid="inq-contact-name" value={form.contact_person.name}
                     onChange={(e) => setForm({ ...form, contact_person: { ...form.contact_person, name: e.target.value } })}
                     className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Designation</Label>
              <Input value={form.contact_person.designation || ""}
                     onChange={(e) => setForm({ ...form, contact_person: { ...form.contact_person, designation: e.target.value } })}
                     className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Phone</Label>
              <Input value={form.contact_person.phone || ""}
                     onChange={(e) => setForm({ ...form, contact_person: { ...form.contact_person, phone: e.target.value } })}
                     className="mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input value={form.contact_person.email || ""}
                     onChange={(e) => setForm({ ...form, contact_person: { ...form.contact_person, email: e.target.value } })}
                     className="mt-1 font-mono" />
            </div>
          </div>
        </Card>

        {/* Line items */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Cylinder Line Items</div>
            <div className="text-xs text-slate-500">Each line represents one cylinder specification with quantity.</div>
          </div>
          <div className="p-5">
            <LineItemsEditor value={form.line_items} customerId={form.customer_id} onChange={(v) => setForm({ ...form, line_items: v })} />
          </div>
        </Card>

        {/* Remarks */}
        <Card className="border-slate-200">
          <div className="p-5">
            <Label className="text-xs uppercase tracking-wider text-slate-600">Remarks</Label>
            <Textarea data-testid="inq-remarks" value={form.remarks || ""} rows={2}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1" />
          </div>
        </Card>

        <div className="flex justify-end gap-2 sticky bottom-4">
          <Button type="button" variant="outline" onClick={() => nav("/inquiries")}>Cancel</Button>
          <Button type="submit" data-testid="inq-submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create Inquiry")}
          </Button>
        </div>
      </form>
    </div>
  );
}
